import React, {useContext, useState} from 'react';
import {Form, Input, InputNumber, Select, Button} from 'antd';
import {internalApi} from "../../../common/axios";
import {DIALOG_KEY} from "../../../shared/utils/enum";
import {buildUrl} from "../../../shared/utils/net";
import {ShoppingRoadManagerEntity} from "./ShoppingRoadManagerEntity";
import {CancelButton, DeleteButton, SaveButton} from "../../../shared/components/buttons/CustomButtons";
import {useItemKrTextData} from "../../probability/useItemData";
import {AuthContext} from "../../../shared/contexts/authContext";
import IsLoading from "../../../shared/components/IsLoading";
import toast from "react-hot-toast";
import {useQueryClient} from "@tanstack/react-query";
import ItemRewardSlot from "../../../shared/components/rewards/ItemRewardSlot";
import {useShoppingRoadManager} from "./useShoppingRoad";
import ErrorPage from "../../../shared/components/ErrorPage";

const {Option} = Select;

const RewardSlots = ({label, rewards, onChange, itemData}) => {
    const slots = [0, 1, 2].map(i => rewards[i] || {id: 0, type: 0, quantity: 0});

    return (
        <div style={{marginBottom: 12}}>
            <div style={{fontWeight: 500, marginBottom: 6}}>{label}</div>
            <div style={{display: 'flex', gap: 8}}>
                {slots.map((slot, i) => (
                    <div
                        key={i}
                        style={{flex: 1, border: '1px solid #d9d9d9', borderRadius: 4, padding: 4}}
                    >
                        <ItemRewardSlot
                            slot={slot}
                            itemData={itemData}
                            size="middle"
                            onChange={newSlot => {
                                const newSlots = slots.map((s, si) => si === i ? newSlot : s);
                                onChange(newSlots);
                            }}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

const ShoppingRoadManagerEditModal = ({record, handleClose, dialogRef, handleSetDialog, modalOptions}) => {
    const queryClient = useQueryClient();
    const {currentUser} = useContext(AuthContext);
    const {data: itemRaw, isLoading: isItemLoading, error: itemError} = useItemKrTextData();
    const {data: shoppingRoadData, isLoading: isShoppingRoadDataLoading, error: shoppingRoadDataError} = useShoppingRoadManager();
    const itemData = itemRaw?.[currentUser?.target] || [];
    const [rows, setRows] = useState(
        record && record.seq ? new ShoppingRoadManagerEntity(record) : new ShoppingRoadManagerEntity({})
    );
    const [selectedTblIdx, setSelectedTblIdx] = useState(null);

    const tbl = modalOptions?.tbl || {};
    const tblKeys = Object.keys(tbl).sort((a, b) => Number(a) - Number(b));

    const handleSetRows = (key, value) => {
        setRows((prevState) => new ShoppingRoadManagerEntity({...prevState, [key]: value}));
    };

    const handleApplyTbl = () => {
        if (!selectedTblIdx && selectedTblIdx !== 0) return;
        const tblEntry = tbl[selectedTblIdx];
        if (!tblEntry) return;

        const convertRewards = (rewards) => (rewards || []).map(r => ({
            type: r.type || 0,
            id: r.id || 0,
            quantity: r.quantity || 0,
        }));

        setRows(new ShoppingRoadManagerEntity({
            ...rows,
            restartDia: tblEntry.restartDia || 0,
            refreshDia: tblEntry.refreshDia || 0,
            refreshFree: tblEntry.refreshFree || 0,
            easyRewards: convertRewards(tblEntry.easyRewards),
            normalRewards: convertRewards(tblEntry.normalRewards),
            hardRewards: convertRewards(tblEntry.hardRewards),
        }));
    };

    const handleSave = (e) => {
        e.preventDefault();
        const entity = rows instanceof ShoppingRoadManagerEntity ? rows : new ShoppingRoadManagerEntity(rows);
        if (typeof entity.isInvalidate === 'function' && entity.isInvalidate()) return;

        return internalApi.post('events/shopping_road/manager', {data: entity.toJson()}).then((res) => {
            toast.success(res.data.message);
            queryClient.invalidateQueries({queryKey: ['shoppingRoadManager']});
            handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
            handleClose(true);
        }).catch((err) => {
            console.error(err);
            toast.error('저장 실패');
        });
    };

    const handleDelete = (e) => {
        e.preventDefault();
        const currentDialog = dialogRef.current;
        if (currentDialog.confirmText === 'DELETE') {
            return internalApi.delete(buildUrl('events/shopping_road/manager', {seq: rows.seq})).then((res) => {
                toast.success(res.data.message);
                queryClient.invalidateQueries({queryKey: ['shoppingRoadManager']});
                handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
                handleClose(true);
            }).catch((err) => {
                toast.error('삭제 실패');
                console.error(err);
            });
        } else {
            toast.error("잘못된 확인 문자입니다. DELETE 를 입력하세요.");
            handleSetDialog([{key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
        }
    };

    if (isItemLoading || isShoppingRoadDataLoading) return <IsLoading/>;
    const error = itemError || shoppingRoadDataError;
    if (error) return <ErrorPage error={error} />;

    return (
        <div style={{marginTop: 26}}>
            <Form layout="vertical">
                <div style={{display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 16}}>
                    <Form.Item label="데이터 프리셋 로드" style={{flex: 1, marginBottom: 0}}>
                        <Select
                            value={selectedTblIdx}
                            onChange={setSelectedTblIdx}
                            placeholder="CSV 인덱스 선택"
                            showSearch
                        >
                            {tblKeys.map(k => (
                                <Option key={k} value={k}>
                                    {k}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item style={{marginBottom: 0}}>
                        <Button onClick={handleApplyTbl}>적용</Button>
                    </Form.Item>
                </div>

                <div style={{display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 8}}>
                    <Form.Item label="이벤트 패스 번호" style={{width: 150}}>
                        <Select
                            value={rows.eventPass === undefined ? shoppingRoadData.passIdxList[0] : rows.eventPass}
                            onChange={value => handleSetRows('eventPass', value)}
                            placeholder="이벤트 패스 번호 선택"
                            showSearch
                        >
                            {shoppingRoadData.passIdxList.map(k => (
                                <Option key={k} value={k}>
                                    {k}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label="재시작 비용 (다이아)" style={{width: 150}}>
                        <InputNumber
                            min={0}
                            value={rows.restartDia}
                            onChange={value => handleSetRows('restartDia', value)}
                            style={{width: '100%'}}
                        />
                    </Form.Item>
                    <Form.Item label="재선택 비용 (다이아)" style={{width: 150}}>
                        <InputNumber
                            min={0}
                            value={rows.refreshDia}
                            onChange={value => handleSetRows('refreshDia', value)}
                            style={{width: '100%'}}
                        />
                    </Form.Item>
                    <Form.Item label="무료 재선택 횟수" style={{width: 150}}>
                        <InputNumber
                            min={0}
                            value={rows.refreshFree}
                            onChange={value => handleSetRows('refreshFree', value)}
                            style={{width: '100%'}}
                        />
                    </Form.Item>
                    <Form.Item label="리소스 인덱스" style={{width: 150}}>
                        <Select
                            value={rows.resourceIdx }
                            onChange={value => handleSetRows('resourceIdx', value)}
                            placeholder="리소스 인덱스 선택"
                            showSearch
                        >
                            {shoppingRoadData.resourceIdxList.map(k => (
                                <Option key={k} value={k}>
                                    {k}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </div>

                <RewardSlots
                    label="쉬움 단계"
                    rewards={rows.easyRewards}
                    onChange={newSlots => handleSetRows('easyRewards', newSlots)}
                    itemData={itemData}
                />
                <RewardSlots
                    label="보통 단계"
                    rewards={rows.normalRewards}
                    onChange={newSlots => handleSetRows('normalRewards', newSlots)}
                    itemData={itemData}
                />
                <RewardSlots
                    label="어려움 단계"
                    rewards={rows.hardRewards}
                    onChange={newSlots => handleSetRows('hardRewards', newSlots)}
                    itemData={itemData}
                />

                <Form.Item label="Memo" style={{marginTop: 8}}>
                    <Input
                        value={rows.memo}
                        onChange={e => handleSetRows('memo', e.target.value)}
                        style={{width: '100%'}}
                    />
                </Form.Item>

                <div style={{marginTop: 16}}>
                    <SaveButton onClick={handleSave} handleSetDialog={handleSetDialog}/>
                    <CancelButton onClick={handleClose}/>
                    <DeleteButton
                        onClick={handleDelete}
                        handleSetDialog={handleSetDialog}
                        display={rows.seq > 0 ? 'flex' : 'none'}
                    />
                </div>
            </Form>
        </div>
    );
};

export default ShoppingRoadManagerEditModal;